import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Printer } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) =>
  d ? new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—";

const idTypeLabel = (t) =>
  ({ driving_licence: "Driving licence", passport: "Passport", other: "Other photo ID" }[t] || "—");

const Row = ({ label, value }) => (
  <tr>
    <td style={{ padding: "8px 12px 8px 0", color: "#6A5F58", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap", verticalAlign: "top" }}>{label}</td>
    <td style={{ padding: "8px 0", color: "#14100F", fontSize: 14, fontWeight: 500 }}>{value || "—"}</td>
  </tr>
);

/**
 * Printable / save-as-PDF guest check-in record (admin only).
 * Opened from the On-Site tab once a guest submits their online check-in.
 */
const AdminCheckInSheet = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        const { data: res } = await axios.get(`${API}/admin/bookings/${bookingId}/checkin-sheet`);
        if (!disposed) setData(res);
      } catch (e) {
        if (!disposed) {
          if (e?.response?.status === 401) {
            navigate("/admin/login");
            return;
          }
          setError(e?.response?.data?.detail || "Couldn't load this check-in record.");
        }
      }
    })();
    return () => { disposed = true; };
  }, [bookingId, navigate]);

  return (
    <div className="min-h-screen bg-[#F5EFE6]" data-testid="checkin-sheet-page">
      {/* Toolbar — hidden when printing */}
      <div className="no-print flex items-center justify-between px-5 py-4 bg-[#0F0E0E]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-[#F5EFE6] text-[10px] font-brand tracking-[0.16em] uppercase hover:text-[#E4B2A8]"
          data-testid="checkin-sheet-back"
        >
          <ArrowLeft size={13} /> Back
        </button>
        <p className="font-serif text-[#F5EFE6] text-sm tracking-[0.24em] uppercase">Check-in record</p>
        <button
          type="button"
          onClick={() => window.print()}
          disabled={!data?.submitted}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#B76E79] text-white hover:bg-[#96525E] transition text-[10px] font-brand tracking-[0.14em] uppercase disabled:opacity-40"
          data-testid="checkin-sheet-print"
        >
          <Printer size={13} /> Print / Save PDF
        </button>
      </div>

      {error && (
        <div className="max-w-2xl mx-auto mt-10 bg-white border border-[#DDD1BE] rounded-xl p-8 text-center text-[#B76E79]" data-testid="checkin-sheet-error">
          {error}
        </div>
      )}
      {!error && !data && (
        <div className="text-center py-16 text-[#6A5F58]" data-testid="checkin-sheet-loading">
          <Loader2 className="animate-spin inline mr-2" size={16} /> Loading…
        </div>
      )}

      {data && !data.submitted && (
        <div className="max-w-2xl mx-auto mt-10 bg-white border border-[#DDD1BE] rounded-xl p-8 text-center" data-testid="checkin-sheet-empty">
          <p className="font-serif text-xl text-[#14100F]">No check-in form yet</p>
          <p className="text-sm text-[#6A5F58] mt-2">
            {data.guest_name || "This guest"} hasn't completed their online check-in. Send the check-in link from the On-Site tab first.
          </p>
        </div>
      )}

      {data && data.submitted && (
        <div className="max-w-2xl mx-auto my-8 bg-white border border-[#DDD1BE] rounded-xl p-8 md:p-10 print-sheet-body" data-testid="checkin-sheet">
          <div className="border-b-2 border-[#0F0E0E] pb-4 mb-6">
            <p className="font-serif text-2xl text-[#14100F]">Walnut Lakes Holiday Park</p>
            <p className="text-[11px] text-[#6A5F58] mt-1">
              Guest check-in record · Main Road, Algarkirk, Boston PE20 2LQ · 07982 844201
            </p>
          </div>

          <table className="w-full">
            <tbody>
              <Row label="Lead guest" value={data.guest_name} />
              <Row label="Stay" value={`${data.unit_label} · ${fmtDate(data.start_date)} → ${fmtDate(data.end_date)}`} />
              <Row label="Party size" value={`${data.guests} guest${data.guests > 1 ? "s" : ""}${data.dogs ? ` · ${data.dogs} dog${data.dogs > 1 ? "s" : ""}` : ""}`} />
              <Row label="Contact number" value={data.guest_phone} />
              <Row label="Emergency contact" value={data.emergency_phone} />
              <Row label="Email" value={data.guest_email} />
              <Row label="Vehicle reg" value={data.vehicle_reg} />
              <Row label="Photo ID type" value={idTypeLabel(data.id_type)} />
              <Row
                label="Form submitted"
                value={data.submitted_at ? new Date(data.submitted_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—"}
              />
              {data.checked_in_at && (
                <Row label="Arrived on-site" value={new Date(data.checked_in_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })} />
              )}
            </tbody>
          </table>

          {(data.party || []).length > 0 && (
            <div className="mt-8">
              <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-2">Party members</p>
              <table className="w-full border border-[#DDD1BE] rounded-lg overflow-hidden" data-testid="checkin-sheet-party">
                <thead>
                  <tr className="bg-[#F5EFE6]">
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.1em] text-[#6A5F58]">Guest</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.1em] text-[#6A5F58]">Fishing</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.1em] text-[#6A5F58]">Licence</th>
                  </tr>
                </thead>
                <tbody>
                  {data.party.map((p, i) => (
                    <tr key={i} className="border-t border-[#EFE6D8]">
                      <td className="px-3 py-2 text-sm text-[#14100F] font-medium">{p.name}{i === 0 ? " (lead)" : ""}</td>
                      <td className="px-3 py-2 text-sm text-[#3A322E]">{p.fishing ? "Yes" : "—"}</td>
                      <td className="px-3 py-2 text-sm">{p.fishing ? (p.license_url ? "Photo on file" : "Not provided") : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(data.fishing || []).length > 0 && (
            <div className="mt-6">
              <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-2">Fishing day passes</p>
              <table className="w-full border border-[#DDD1BE] rounded-lg overflow-hidden" data-testid="checkin-sheet-fishing">
                <thead>
                  <tr className="bg-[#F5EFE6]">
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.1em] text-[#6A5F58]">Day</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.1em] text-[#6A5F58]">Rods</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.1em] text-[#6A5F58]">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {data.fishing.map((f, i) => (
                    <tr key={i} className="border-t border-[#EFE6D8]">
                      <td className="px-3 py-2 text-sm text-[#14100F]">{fmtDate(f.date)}</td>
                      <td className="px-3 py-2 text-sm text-[#3A322E]">{f.rods} rod{f.rods > 1 ? "s" : ""}</td>
                      <td className="px-3 py-2 text-sm text-[#3A322E]">{Number(f.price) > 0 ? `£${Number(f.price).toFixed(2)}` : "Included (lodge)"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.fishing.some((f) => Number(f.price) > 0) && (
                <p className="mt-2 text-sm">
                  {data.fishing_due > 0 ? (
                    <span className="font-semibold text-[#B3394F]">£{data.fishing_due.toFixed(2)} still to collect</span>
                  ) : (
                    <span className="font-semibold text-[#3B8C4E]">Fishing passes paid (£{(data.fishing_paid_amount || 0).toFixed(2)})</span>
                  )}
                </p>
              )}
            </div>
          )}

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-2">Photo ID ({idTypeLabel(data.id_type)})</p>
              {data.id_document_url ? (
                <img
                  src={`${BACKEND}${data.id_document_url}`}
                  alt="Photo ID"
                  className="w-full rounded-lg border border-[#DDD1BE] object-contain max-h-72 bg-[#F5EFE6]"
                  data-testid="checkin-sheet-id-img"
                />
              ) : (
                <p className="text-xs text-[#6A5F58] border border-dashed border-[#DDD1BE] rounded-lg p-4">Not provided</p>
              )}
            </div>
            {(data.party || []).filter((p) => p.fishing).map((p, i) => (
              <div key={i}>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] mb-2">Fishing licence — {p.name}</p>
                {p.license_url ? (
                  <img
                    src={`${BACKEND}${p.license_url}`}
                    alt={`Fishing licence — ${p.name}`}
                    className="w-full rounded-lg border border-[#DDD1BE] object-contain max-h-72 bg-[#F5EFE6]"
                    data-testid={`checkin-sheet-license-img-${i}`}
                  />
                ) : (
                  <p className="text-xs text-[#6A5F58] border border-dashed border-[#DDD1BE] rounded-lg p-4">Not provided</p>
                )}
              </div>
            ))}
          </div>

          <p className="mt-8 pt-4 border-t border-[#EFE6D8] text-[10px] text-[#6A5F58]">
            Booking ref {data.id} · Printed {new Date().toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })} · Stored securely, used only for this stay.
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminCheckInSheet;
